import { useMemo, useState } from "react";
import AzureConfigsPageView from "./AzureConfigsPageView";
import useCustomEffect from "../../../hooks/useCustomEffect";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import { useAuthContext } from "../../../context/AuthContext";
import { useLanguageContext } from "../../../context/LanguageContext";

function AzureConfigsPage() {
  const authContext = useAuthContext();
  const languageContext = useLanguageContext();
  const callEvaApi = useEvaApi();
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const defaultLocationIdValues = useMemo(
    () =>
      authContext.auth?.user?.company?.locations?.map((location) => ({
        azure_value: `Azure ${location.name}`,
        location_id: location.id,
      })) || [],
    [authContext.auth?.user?.company?.locations],
  );

  const defaultLanguageIdValues = useMemo(
    () =>
      languageContext.activeLanguages.map((language) => ({
        azure_value: `Azure ${language.label}`,
        language_id: language.id,
      })) || [],
    [languageContext.activeLanguages],
  );

  const [configData, setConfigData] = useState({
    id: null,
    active: false,
    tenant_id: "",
    client_id: "",
    client_secret: "",
    employee_azure_group_id: "",
    connect_employees_to_users: false,
    field_mappings: [
      {
        entity: "user",
        field: "name",
        azure_field: "displayName",
        value_mappings: [],
      },
      {
        entity: "user",
        field: "location_id",
        azure_field: "officeLocation",
        value_mappings: defaultLocationIdValues,
      },
      {
        entity: "user",
        field: "language_id",
        azure_field: "preferredLanguage",
        value_mappings: defaultLanguageIdValues,
      },

      {
        entity: "employee",
        field: "location_id",
        azure_field: "officeLocation",
        value_mappings: defaultLocationIdValues,
      },
      {
        entity: "employee",
        field: "first_name",
        azure_field: "givenName",
        value_mappings: [],
      },
      {
        entity: "employee",
        field: "last_name",
        azure_field: "surname",
        value_mappings: [],
      },
      {
        entity: "employee",
        field: "phone_number",
        azure_field: "mobilePhone",
        value_mappings: [],
      },
    ],
    roles: [],
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * initialize the component
   */
  useCustomEffect(() => {
    fetchConfigData();
    fetchDepartments();
    fetchRoles();
  }, []);

  /**
   * This method will fetch the Azure config data from the API
   */
  async function fetchConfigData() {
    await callEvaApi(
      new EvaApiCall("integrations/azure/configs")
        .setLoadingGroup("azure_configs_page_fetch")
        .setAlertError(true)
        .setParams(getConfigFetchParams())
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData(getConfigDataFromApiResponse(data));
        }),
    );
  }

  /**
   * This method will fetch the departments from the API
   */
  async function fetchDepartments() {
    await callEvaApi(
      new EvaApiCall("departments")
        .setLoadingGroup("azure_configs_page_fetch")
        .setAlertError(true)
        .setOnSuccess((response) => {
          setDepartments(response.data.data.records);
        }),
    );
  }

  /**
   * This method will fetch the roles from the API
   */
  async function fetchRoles() {
    await callEvaApi(
      new EvaApiCall("roles")
        .setLoadingGroup("azure_configs_page_fetch")
        .setAlertError(true)
        .setOnSuccess((response) => {
          setRoles(response.data.data.records);
        }),
    );
  }

  /**
   * This method will update the active state on input change
   * @param {bool} value
   */
  function onActiveChange(value) {
    setConfigData({
      ...configData,
      active: value,
    });
  }

  /**
   * This method will update the tenant id on input change
   * @param {event} e
   */
  function onTenantIdChange(e) {
    setConfigData({
      ...configData,
      tenant_id: e.target.value,
    });
  }

  /**
   * This method will update the client id on input change
   * @param {event} e
   */
  function onClientIdChange(e) {
    setConfigData({
      ...configData,
      client_id: e.target.value,
    });
  }

  /**
   * This method will update the client secret on input change
   * @param {event} e
   */
  function onClientSecretChange(e) {
    setConfigData({
      ...configData,
      client_secret: e.target.value,
    });
  }

  /**
   * This method will update the employee azure group id on input change
   * @param {event} e
   */
  function onEmployeeAzureGroupIdChange(e) {
    setConfigData({
      ...configData,
      employee_azure_group_id: e.target.value,
    });
  }

  /**
   * This method will update the connect_employees_to_users on input change
   * @param {bool} value
   */
  function onConnectEmployeesToUsersChange(value) {
    setConfigData({
      ...configData,
      connect_employees_to_users: value,
    });
  }

  /**
   * This method will sync the data with Azure by calling the sync API endpoint
   */
  async function onSync() {
    await callEvaApi(
      new EvaApiCall("integrations/azure/sync")
        .setMethod("POST")
        .setLoadingGroup("azure_configs_page_sync")
        .setAlertError(true)
        .setAlertSuccess(true),
    );
  }

  /**
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall("integrations/azure/configs")
        .setMethod("PUT")
        .setData(configData)
        .setParams(getConfigFetchParams())
        .setLoadingGroup("azure_configs_page_save")
        .setAlertError(true)
        .setErrorState(setSubmitError)
        .setAlertSuccess(true)
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData(getConfigDataFromApiResponse(data));
        }),
    );
  }

  /**
   * This method will return the params for fetching the config data
   * @returns {object}
   */
  function getConfigFetchParams() {
    return {
      with_azure_config_field_mappings_data: true,
      with_azure_config_field_mapping_value_mappings_data: true,
      with_azure_config_roles_data: true,
    };
  }

  /**
   * This method will map the API response to the config data structure
   * @param {object} data
   * @returns {object}
   */
  function getConfigDataFromApiResponse(data) {
    return {
      id: data.id,
      active: data.active,
      tenant_id: data.tenant_id,
      client_id: data.client_id,
      client_secret: data.client_secret,
      employee_azure_group_id: data.employee_azure_group_id,
      connect_employees_to_users: data.connect_employees_to_users,
      field_mappings: data.field_mappings.map((mapping) => ({
        entity: mapping.entity,
        field: mapping.field,
        azure_field: mapping.azure_field,
        value_mappings: (mapping.value_mappings ?? []).map((valueMapping) => ({
          azure_value: valueMapping.azure_value,
          [mapping.field]: valueMapping[mapping.field.replace("_id", "")],
        })),
      })),
      roles: data.roles.map((dataRole) => ({
        role_id: dataRole.role,
        azure_group_id: dataRole.azure_group_id,
      })),
    };
  }

  return (
    <AzureConfigsPageView
      configData={configData}
      departments={departments}
      roles={roles}
      submitError={submitError}
      setConfigData={setConfigData}
      onSync={onSync}
      onSubmit={onSubmit}
      onActiveChange={onActiveChange}
      onTenantIdChange={onTenantIdChange}
      onClientIdChange={onClientIdChange}
      onClientSecretChange={onClientSecretChange}
      onEmployeeAzureGroupIdChange={onEmployeeAzureGroupIdChange}
      onConnectEmployeesToUsersChange={onConnectEmployeesToUsersChange}
    />
  );
}

export default AzureConfigsPage;
